import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import Sidebar from "../Layout/Sidebar";
import Header from "../Layout/Header";
import "./TenantContacts.css";
import { API_BASE } from "../../../ApiUrl";
import TenantPeopleTabs from "./TenantPeopleTabs";
import OrganizationTabs from "../Organizations/OrganizationTabs";

const TenantContacts = () => {
  const navigate = useNavigate();

  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

const fetchContacts = useCallback(async () => {
  setLoading(true);
  setError("");

  try {
    const storedTenantUser =
      localStorage.getItem("tenant_user");

    if (!storedTenantUser) {
      throw new Error("Tenant information missing.");
    }

    const tenantUser = JSON.parse(storedTenantUser);

    if (!tenantUser.tenant_id) {
      throw new Error("Tenant ID missing.");
    }

    const response = await fetch(
      `${API_BASE}/tenant-records/`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          tenant_id: Number(tenantUser.tenant_id),
          tables: ["tenant_contacts"],
        }),
      }
    );

    if (!response.ok) {
      throw new Error(
        `HTTP error! status: ${response.status}`
      );
    }

    const result = await response.json();

    console.log(
      "Tenant contacts response:",
      result
    );

    let data = result;

    if (result?.data) {
      data = result.data;
    }

    // Handle table-wise response
    if (data?.tenant_contacts) {
      data = data.tenant_contacts;
    }

    if (Array.isArray(data)) {
      setContacts(data);
    } else {
      setContacts([]);
    }
  } catch (error) {
    console.error(
      "Error fetching tenant contacts:",
      error
    );

    setError(
      error.message ||
        "Failed to load tenant contacts."
    );

    setContacts([]);
  } finally {
    setLoading(false);
  }
}, []);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  const handleAddClick = () => {
    navigate("/admin/contacts/add");
  };

  const handleEdit = (contact) => {
    navigate("/admin/contacts/add", {
      state: { editContact: contact }
    });
  };

  const handleDelete = async (contact) => {
    const confirmResult = await Swal.fire({
      icon: "warning",
      title: "Delete Tenant Contact?",
      html: `Are you sure you want to delete <strong>${
        contact.full_name || "this contact"
      }</strong>?<br/>This action cannot be undone.`,
      showCancelButton: true,
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#6b7280",
      reverseButtons: true
    });

    if (!confirmResult.isConfirmed) return;

    Swal.fire({
      title: "Deleting...",
      text: "Please wait while the contact is being deleted.",
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    try {
      const response = await fetch(
        `${API_BASE}/tenant-contacts/${contact.id}/`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json"
          }
        }
      );

      const data =
        response.status === 204
          ? null
          : await response.json().catch(() => null);

      if (!response.ok) {
        let message = "";

        if (data?.errors && typeof data.errors === "object") {
          message = Object.entries(data.errors)
            .map(
              ([field, errors]) =>
                `${field}: ${
                  Array.isArray(errors) ? errors.join(", ") : errors
                }`
            )
            .join(" | ");
        } else {
          message =
            data?.message ||
            data?.detail ||
            `Request failed with status ${response.status}`;
        }

        throw new Error(message);
      }

      setContacts((prev) => prev.filter((item) => item.id !== contact.id));

      Swal.fire({
        icon: "success",
        title: "Deleted!",
        text: "Contact has been deleted successfully.",
        confirmButtonColor: "#3085d6",
        timer: 1800,
        timerProgressBar: true,
        showConfirmButton: false
      });

      fetchContacts();
    } catch (error) {
      console.error("Error deleting contact:", error);

      Swal.fire({
        icon: "error",
        title: "Delete Failed",
        text: error.message || "Failed to delete contact.",
        confirmButtonColor: "#d33"
      });
    }
  };

  const getContactTypeLabel = (type) => {
    switch (type) {
      case "ORG_ADMIN":
        return "Organisation Admin";
      case "TECHNICAL":
        return "Technical";
      case "PROJECT":
        return "Project";
      case "FINANCE":
        return "Finance";
      case "LEGAL":
        return "Legal";
      case "SECURITY":
        return "Security";
      default:
        return type || "—";
    }
  };

  const formatDate = (date) => {
    if (!date) return "—";

    const parsedDate = new Date(date);

    if (Number.isNaN(parsedDate.getTime())) {
      return date;
    }

    return parsedDate.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  };

  return (
    <div className="app-layout">
      <Sidebar />

      <div className="main-content">
        <Header />

        <div className="tenant-contacts-page">
          <OrganizationTabs/>
          <TenantPeopleTabs />

          <div className="tenant-contacts-header">
            <div>
              <h2>Contacts</h2>
              <p>Manage tenant contacts and their validity.</p>
            </div>

            <button
              type="button"
              className="tenant-contacts-add-btn"
              onClick={handleAddClick}
            >
              <i className="fas fa-plus"></i>
              Add Tenant Contact
            </button>
          </div>
           
          {error && (
            <div className="tenant-contacts-error">
              {error}
            </div>
          )}

          <div className="tenant-contacts-table-container">
            <table className="tenant-contacts-table">
              <colgroup>
                <col className="contact-col-number" />
                <col className="contact-col-type" />
                <col className="contact-col-name" />
                <col className="contact-col-details" />
                <col className="contact-col-date" />
                <col className="contact-col-date" />
                <col className="contact-col-status" />
                <col className="contact-col-actions" />
              </colgroup>

              <thead>
                <tr>
                  <th>#</th>
                  <th>Contact Type</th>
                  <th>Full Name</th>
                  <th>Contact Details</th>
                  <th>Effective From</th>
                  <th>Effective To</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>

              <tbody>
                {loading && (
                  <tr>
                    <td colSpan="8" className="tenant-contacts-empty-cell">
                      Loading tenant contacts...
                    </td>
                  </tr>
                )}

                {!loading && contacts.length === 0 && (
                  <tr>
                    <td colSpan="8" className="tenant-contacts-empty-cell">
                      No contacts found.
                    </td>
                  </tr>
                )}

                {!loading &&
                  contacts.map((contact, index) => (
                    <tr key={contact.id}>
                      <td className="tenant-contacts-number-cell">
                        {index + 1}
                      </td>

                      <td>
                        <span className="tenant-contacts-type-badge">
                          {getContactTypeLabel(contact.contact_type)}
                        </span>
                      </td>

                      <td className="tenant-contacts-name-cell">
                        {contact.full_name || "—"}
                      </td>

                      <td>
                        <div className="tenant-contacts-details">
                          <div className="tenant-contacts-detail-row">
                            <i className="fas fa-envelope"></i>
                            <span title={contact.email || ""}>
                              {contact.email || "—"}
                            </span>
                          </div>

                          <div className="tenant-contacts-detail-row">
                            <i className="fas fa-phone"></i>
                            <span title={contact.phone || ""}>
                              {contact.phone || "—"}
                            </span>
                          </div>
                        </div>
                      </td>

                      <td className="tenant-contacts-date-cell">
                        {formatDate(contact.effective_from)}
                      </td>

                      <td className="tenant-contacts-date-cell">
                        {formatDate(contact.effective_to)}
                      </td>

                      <td>
                        <span
                          className={`tenant-contacts-status-badge ${
                            contact.is_active
                              ? "tenant-contacts-status-active"
                              : "tenant-contacts-status-inactive"
                          }`}
                        >
                          {contact.is_active ? "Active" : "Inactive"}
                        </span>
                      </td>

                      <td>
                        <div className="tenant-contacts-actions">
                          <button
                            type="button"
                            className="tenant-contacts-action-btn tenant-contacts-edit-btn"
                            title="Edit"
                            onClick={() => handleEdit(contact)}
                          >
                            <i className="fas fa-pen"></i>
                          </button>

                          <button
                            type="button"
                            className="tenant-contacts-action-btn tenant-contacts-delete-btn"
                            title="Delete"
                            onClick={() => handleDelete(contact)}
                          >
                            <i className="fas fa-trash-alt"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TenantContacts;